import { motion } from "framer-motion";
import moment from "moment";
import { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { GetMissingTooth } from "../config/preauthorizationApi";

const PreauthorizationMedicalDetailsNested = () => {
  const dispatch = useDispatch();
  const {
    preauthorizationRequest,
    viewPreauthorizationInfo,
    missingTeethListReason,
  } = useSelector((state) => state.preauthorization);
  const { viewClaimInfo, claimRequestData } = useSelector(
    (state) => state.claim
  );

  useEffect(() => {
    if (missingTeethListReason?.length === 0) {
      dispatch(GetMissingTooth());
    }
  }, [missingTeethListReason]);

  const cliamPreauthorization =
    preauthorizationRequest?.entry?.find(
      (e) => e.resource?.resourceType === "Claim"
    )?.resource ||
    claimRequestData?.entry?.find((e) => e.resource?.resourceType === "Claim")
      ?.resource;

  const aspectCondition = cliamPreauthorization?.supportingInfo?.find(
    (item) => item?.code?.coding?.[0]?.code === "aspects of condition"
  );

  const missingTooth = cliamPreauthorization?.supportingInfo?.find(
    (item) => item?.category?.coding?.[0]?.code === "missingtooth"
  );

  const getMedicalText = (code) => {
    const item = cliamPreauthorization?.supportingInfo.find((entry) =>
      entry.category?.coding?.find((coding) => coding.code === code)
    );

    return item?.code?.text || item?.valueString || null;
  };

  return (
    <motion.div
      style={{ border: "2px solid #d9f8eb" }}
      className="rounded p-3 position-relative mt-3 mb-3"
      initial={{ opacity: 0, y: 50 }} // Starts faded out and moved down
      animate={{ opacity: 1, y: 0 }} // Fades in and moves to place
      exit={{ opacity: 0, y: 50 }} // Optional: for exit animation
      transition={{ duration: 0.5 }} // Smooth transition
    >
      {/* Floating Badge Header */}
      <div className="position-absolute top-0 start-40 translate-middle-y px-2 d-none d-md-block">
        <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
          <span className="fs-11">Medical Details</span>
        </span>
      </div>
      <Row>
        <Col xs={12}>
          {/* Chief Complaint */}
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">Chief Complaint</span>
            </Col>
            <Col xs={12} md={9}>
              <span className="fs-10 fw-normal text-800">
                {getMedicalText("chief-complaint")}
              </span>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">
                Aspects Of Condition (Onset)
              </span>
            </Col>
            <Col xs={12} md={9}>
              <span className="fs-10 fw-normal text-800">
                {aspectCondition?.timingDate && aspectCondition?.timingDate}
                {aspectCondition?.timingPeriod &&
                  `From ${aspectCondition?.timingPeriod?.start} To ${aspectCondition?.timingPeriod?.end}`}
              </span>
            </Col>
          </Row>
          <hr />

          {/* Significant Signs */}
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">Significant Signs</span>
            </Col>
            <Col xs={12} md={9}>
              <span className="fs-10 fw-normal text-800">
                {viewPreauthorizationInfo?.other_fields?.sgnfcntsign ||
                  viewClaimInfo?.other_fields?.sgnfcntsign}
              </span>
            </Col>
          </Row>
          <hr />

          {/* Other Conditions */}
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">Other Conditions</span>
            </Col>
            <Col xs={12} md={9}>
              <span className="fs-10 fw-normal text-800">
                {viewPreauthorizationInfo?.other_fields?.othrconditions ||
                  viewClaimInfo?.other_fields?.othrconditions ||
                  ""}
              </span>
            </Col>
          </Row>
          <hr />

          {/* Clinical Data */}
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">Clinical Data</span>
            </Col>
            <Col xs={12} md={9}>
              <span className="fs-10 fw-normal text-800">
                {viewPreauthorizationInfo?.other_fields?.clncldata ||
                  viewClaimInfo?.other_fields?.clncldata ||
                  ""}
              </span>
            </Col>
          </Row>
          <hr />

          {/* Treatment Plan */}
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">Missing Tooth</span>
            </Col>
            <Col xs={12} md={9}>
              {missingTooth && (
                <Row className="fs-10 fw-normal text-800">
                  <Col xs={4}>
                    Number : {missingTooth?.code?.coding?.[0]?.code}
                  </Col>
                  <Col xs={4}>
                    Date :{" "}
                    {moment(missingTooth?.timingDate).format("DD-MM-YYYY")}
                  </Col>
                  <Col xs={4}>
                    Reason:{" "}
                    {
                      missingTeethListReason?.find(
                        (item) =>
                          item?.msngtoothcde_code ===
                          missingTooth?.reason?.coding?.[0]?.code
                      )?.msngtoothcde_name
                    }
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
          <hr />
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">Treatment Plan</span>
            </Col>
            <Col xs={12} md={9}>
              <span className="fs-10 fw-normal text-800">
                {getMedicalText("treatment-plan")}
              </span>
            </Col>
          </Row>
          <hr />

          {/* Patient History */}
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">Patient History</span>
            </Col>
            <Col xs={12} md={9}>
              <span className="fs-10 fw-normal text-800">
                {getMedicalText("patient-history")}
              </span>
            </Col>
          </Row>
          <hr />

          {/* Physical Examination */}
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">Physical Examination</span>
            </Col>
            <Col xs={12} md={9}>
              <span className="fs-10 fw-normal text-800">
                {getMedicalText("physical-examination")}
              </span>
            </Col>
          </Row>
          <hr />

          {/* History Of Present Illness */}
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">History Of Present Illness</span>
            </Col>
            <Col xs={12} md={9}>
              <span className="fs-10 fw-normal text-800">
                {getMedicalText("history-of-present-illness")}
              </span>
            </Col>
          </Row>
        </Col>
      </Row>
    </motion.div>
  );
};

export default PreauthorizationMedicalDetailsNested;
